/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

/**
 * Override this handler to parse the product documents to find product videos
 * Override this handler to add some custom expands to load-Product request
 */

import { parseProductCustomState } from "@eisinc/App/Handlers/Data/Products/LoadProductByPath";
import setProductVideos from "@eisinc/App/Handlers/Pages/ProductDetails/SetProductVideos";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { getProductCustomState } from "@eisinc/App/Store/Selectors/ProductDetailsSelectors";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { addToChainAfter, Handler, replaceInChain,  } from "@insite/client-framework/HandlerCreator";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";

import {
    chain,
    DispatchUpdateVariantSelection,
    LoadRealTimePricing,
    SetProductInfo
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateVariantSelection";

interface Parameter {
    variantTraitId?: string;
    traitValueId?: string;
    productId: string;
}

interface Props {
    variantSelection: SafeDictionary<string>;
    variantSelectionCompleted: boolean;
    selectedProductId?: string;
    productInfo?: ProductInfo;
}

type HandlerType = Handler<Parameter, Props>;

export const XngSetProductInfo: HandlerType = props => {
    const state = props.getState();
    const { productInfosById, selectedProductId } = state.pages.productDetails;
    if (!productInfosById) {
        throw new Error("productInfosById was undefined in the productDetailsState");
    }

    const currentSelectedProductId = selectedProductId ?? props.parameter.productId;
    if (!currentSelectedProductId) {
        throw new Error("selectedProductId was undefined in the productDetailsState");
    }

    if (!props.selectedProductId) {
        throw new Error("selectedProductId was undefined in the props for UpdateVariantSelection");
    }

    const currentProductInfo = productInfosById[currentSelectedProductId];
    if (!currentProductInfo) {
        throw new Error(`There was no productInfoById for the id ${currentSelectedProductId}`);
    }
    const newProductInfo = productInfosById[props.selectedProductId];
    if (!newProductInfo) {
        throw new Error(`There was no productInfoById for the id ${props.selectedProductId}`);
    }
    const product = getProductState(state, props.selectedProductId).value;
    if (!product) {
        throw new Error(`There was no product found for the id ${props.selectedProductId}`);
    }

    // remove min/max-check on the qty for variants
    props.productInfo = {
        ...newProductInfo,
        unitOfMeasure: product.unitOfMeasures!.some(o => o.unitOfMeasure === currentProductInfo.unitOfMeasure)
            ? currentProductInfo.unitOfMeasure
            : newProductInfo.unitOfMeasure,
    };
};

export const CustomDispatchUpdateVariantSelection: HandlerType = props => {
    if (props.productInfo && props.variantSelectionCompleted) {
        const state = props.getState() as CustomAppState;
        const initializeCustomState = !getProductCustomState(state, props.productInfo.productId);
        if (initializeCustomState) {
            const variantProductState = getProductState(state, props.productInfo.productId);
            if (variantProductState.value && props.parameter.productId) {
                const variantProductCsutomState = parseProductCustomState(variantProductState.value).customProductState;
                const parentProductCsutomState = getProductCustomState(state, props.parameter.productId);

                // fallback to parent data if the variant childs don't have videos
                if (variantProductCsutomState && variantProductCsutomState.videos.length === 0) {
                    variantProductCsutomState.videos = parentProductCsutomState.videos;
                    variantProductCsutomState.selectedVideoIndex = parentProductCsutomState?.selectedVideoIndex;
                }

                // fallback to parent data if the variant childs don't have attributeGroups
                if (variantProductCsutomState.attributeGroups.length === 0) {
                    variantProductCsutomState.attributeGroups = parentProductCsutomState.attributeGroups;
                }

                variantProductCsutomState!.documentGroups = variantProductCsutomState?.documentGroups?.concat(
                    parentProductCsutomState?.documentGroups,
                );

                props.dispatch(setProductVideos({ customProductState: variantProductCsutomState }));
            }
        }
    }

    props.dispatch({
        type: "Pages/ProductDetails/UpdateVariantSelection",
        variantSelection: props.variantSelection,
        variantSelectionCompleted: props.variantSelectionCompleted,
        selectedProductInfo: props.productInfo!,
    });
};

addToChainAfter(chain, SetProductInfo, LoadRealTimePricing);
replaceInChain(chain, SetProductInfo, XngSetProductInfo);
replaceInChain(chain, DispatchUpdateVariantSelection, CustomDispatchUpdateVariantSelection);
